import { RequestManager } from './managers/requestManager';
import { Session } from './managers/sessionManager';
import {
  MANUAL_SESSION_SAVE_SESSION_KEY,
  OBFUSCATION_SELECTOR_STORAGE_KEY,
  RECORD_ENGINE_STORAGE_KEY,
  SESSION_ID_KEY,
  SESSION_PERSISTANT_PAUSE_KEY,
  SESSION_START_OPTIONS_KEY,
  SESSION_TAGS_STORAGE_KEY,
} from './managers/storageConstants';
import { StorageManager } from './managers/storageManager';
import { ActionModule } from './modules/action';
import { BaseModule } from './modules/base';
import { ClickModule } from './modules/clicks';
import { LoggerModule } from './modules/console';
import { CustomEventsModule } from './modules/customEvents';
import { IdentityUpdaterModule } from './modules/identityUpdater';
import { NavigationModule } from './modules/navigation';
import { FormModule } from './modules/form';
import { PerformanceModule } from './modules/performance';
import { RecordModule } from './modules/record';
import { TagModule } from './modules/tag';
import { Namespace } from './modules/types';
import { UserCommentsModule } from './modules/userComments';
import {
  EventTypes,
  FeedbackPayload,
  FeedbackSubmitPayload,
  FeedbackWidgetPayload,
  StartOptions,
  SupportSystem,
  TrackEvent,
  UserCommentPositioning,
} from './types';
import { hasSessionStorage } from './utils/hasSessionStorage';
import { logDebug, logError, logInfo, logWarn } from './utils/log';

export class Recapt {
  public static DEBUG: boolean = false;

  public static running = false;
  public static hasStartedOnce = false;
  private static persistStorage = false;

  public static apiKey: string | null = null;

  private static _sessionId: string = '';

  private static detectedSupportSystems: SupportSystem[] = [];

  private static currentStartOptions: StartOptions | null = null;

  public static userCommentsInfo: {
    positioning?: UserCommentPositioning;
    enabled: boolean;
  } = {
    enabled: false,
  };

  private static eventListeners: Record<EventTypes, ((data: any) => void)[]> = {
    'session:id:change': [],
    'session:id:set': [],
  };

  private static _modulesToLoad: (new () => BaseModule<any>)[] | null = null;

  private static get modulesToLoad(): (new () => BaseModule<any>)[] {
    if (!this._modulesToLoad) {
      this._modulesToLoad = [
        RecordModule,
        LoggerModule,
        ClickModule,
        NavigationModule,
        TagModule,
        ActionModule,
        CustomEventsModule,
        IdentityUpdaterModule,
        PerformanceModule,
        FormModule,
      ];
    }
    return this._modulesToLoad;
  }

  public static modules: Partial<Record<Namespace, BaseModule<any>>> &
    Iterable<BaseModule<any>> = {
    [Symbol.iterator]() {
      const values = Object.values(this);
      let index = 0;

      return {
        next: () => {
          if (index < values.length)
            return { value: values[index++], done: false };
          return { value: undefined as any, done: true };
        },
      };
    },
  };

  public static session = new Session();

  public static tellListeners(event: EventTypes, data: any) {
    if (!(event in this.eventListeners) || !this.eventListeners[event]) return;
    for (const callback of this.eventListeners[event]) callback(data);
  }

  public static once(event: EventTypes, callback: (data: any) => void) {
    if (!this.eventListeners[event]) this.eventListeners[event] = [];
    this.eventListeners[event].push(callback);
  }

  public static set sessionId(sessionId: string) {
    const previous = this._sessionId;
    this._sessionId = sessionId;

    if (sessionId !== previous)
      this.tellListeners('session:id:change', sessionId);

    this.tellListeners('session:id:set', sessionId);
  }

  public static get sessionId() {
    return this._sessionId;
  }

  public static getModule<T extends BaseModule<any>>(
    namespace: Namespace,
  ): T | undefined {
    return this.modules[namespace] as T;
  }

  public static purge() {
    StorageManager.purge();

    for (const mod of this.modules) {
      mod.purgeState();
    }
  }

  private static debugMode() {
    this.DEBUG = true;
    this.info('Recapt is running in DEBUG mode');
  }

  public static enableUserComments(pos?: UserCommentPositioning) {
    if (this.hasStartedOnce || this.running) {
      this.warn(
        'enableUserComments() can only be called before Recapt has started.',
      );
      return;
    }

    this.userCommentsInfo = {
      positioning: pos,
      enabled: true,
    };
  }

  public static where() {
    return window.location.pathname;
  }

  public static hasSupportSystem(system: SupportSystem) {
    return this.detectedSupportSystems.includes(system);
  }

  public static setDebugNetworkCallbackFunction(fn: (data: any) => void) {
    RequestManager.debugNetworkPrintCallback = fn;
  }

  /**
   * Configures the system to use system-defined painpoints.
   * Must be called before Recapt starts.
   */
  public static useSystemPainpoints() {
    if (this.hasStartedOnce) {
      this.warn(
        'useSystemPainpoints() can only be called before Recapt has started.',
      );
      return;
    }

    RequestManager.painpointMode = 'system';
  }

  /**
   * Saves the current session state.
   * Cannot be used with system painpoints.
   */
  public static saveSession() {
    if (RequestManager.painpointMode === 'system') {
      this.warn('saveSession() cannot be used with useSystemPainpoints()');
      return;
    }

    StorageManager.setItem(MANUAL_SESSION_SAVE_SESSION_KEY, true);

    RequestManager.saveSession = true;
  }

  /**
   * Terminates the current session: flushes pending data to the server,
   * sends a termination signal, then clears all local/session storage.
   */
  public static async terminate() {
    if (!this.running) return;
    this.stop();

    await RequestManager.forceSync();
    await RequestManager.sendTerminateRequest();

    this.clearAllStorage();
  }

  /**
   * Triggers an immediate rrweb FullSnapshot.
   * Called by NavigationModule on SPA navigation so the graphify pipeline
   * gets an up-to-date DOM snapshot without waiting for periodic checkout.
   */
  public static takeFullSnapshot() {
    const recordModule = this.getModule<RecordModule>('record');
    if (recordModule) recordModule.takeFullSnapshot();
  }

  public static addAction(action: string) {
    const actionModule = this.getModule<ActionModule>('actions');
    if (!actionModule) return;
    actionModule.onAction(action);
  }

  public static tag(tag: string) {
    const tagModule = this.getModule<TagModule>('tags');
    if (!tagModule) return;
    tagModule.onTag(tag);
  }

  /**
   * Unified event tracking API.
   * Sends tags, actions, or custom events with optional properties.
   *
   * @example
   * // Send a tag (deduplicated per session)
   * Recapt.track({ event: 'premium_user', type: 'tag' });
   *
   * // Send an action (can repeat)
   * Recapt.track({ event: 'checkout_started', type: 'action' });
   *
   * // Send a custom event with properties (default type)
   * Recapt.track({ event: 'purchase', properties: { amount: 99.99, currency: 'USD' } });
   */
  public static track(trackEvent: TrackEvent) {
    if (!trackEvent || !trackEvent.event) {
      this.warn('track() requires an event name');
      return;
    }

    if (!this.running) {
      this.warn('track() called but Recapt is not running');
      return;
    }

    const eventType = trackEvent.type ?? 'custom';

    switch (eventType) {
      case 'tag':
        this.tag(trackEvent.event);
        break;
      case 'action':
        this.addAction(trackEvent.event);
        break;
      case 'custom':
      default: {
        const customEventsModule =
          this.getModule<CustomEventsModule>('customEvents');
        if (!customEventsModule) {
          this.warn('track() customEvents module not found');
          return;
        }
        this.debug('track() sending custom event:', trackEvent.event);
        customEventsModule.onCustomEvent(
          trackEvent.event,
          trackEvent.properties,
        );
        break;
      }
    }
  }

  /**
   * Submit feedback or control the feedback widget.
   *
   * @example
   * // Submit feedback programmatically (for custom UIs)
   * Recapt.feedback({ message: 'Great feature!' });
   * Recapt.feedback({ message: 'Could be better', rating: 3 });
   *
   * // Show the feedback widget
   * Recapt.feedback({ widget: 'show' });
   * Recapt.feedback({ widget: 'show', position: 'top-right' });
   *
   * // Hide the widget
   * Recapt.feedback({ widget: 'hide' });
   *
   * // Open/close the form
   * Recapt.feedback({ widget: 'open' });
   * Recapt.feedback({ widget: 'close' });
   */
  public static feedback(payload: FeedbackPayload) {
    if (!payload || typeof payload !== 'object') {
      this.debug('feedback() requires a payload object');
      return;
    }

    if (!this.running) {
      this.debug('feedback() called but Recapt is not running');
      return;
    }

    // Check if this is a widget control payload
    if ('widget' in payload) {
      const widgetPayload = payload as FeedbackWidgetPayload;
      this.handleFeedbackWidget(widgetPayload);
      return;
    }

    // Check if this is a feedback submission payload
    if ('message' in payload) {
      const submitPayload = payload as FeedbackSubmitPayload;
      this.handleFeedbackSubmit(submitPayload);
      return;
    }

    this.debug('feedback() payload must contain either "message" or "widget"');
  }

  private static handleFeedbackWidget(payload: FeedbackWidgetPayload) {
    // Get or create the module
    let module = this.getModule<UserCommentsModule>('userComments');

    // If module doesn't exist and we're trying to show, we need to load it
    if (!module && payload.widget === 'show') {
      // Dynamically add the module if not already loaded
      if (!this.userCommentsInfo.enabled) {
        this.userCommentsInfo.enabled = true;
      }

      // Create and initialize the module
      module = new UserCommentsModule();
      module.setup();
      this.modules[module.namespace] = module;
      module.onStart();
    }

    if (!module) {
      this.debug('feedback() userComments module not available');
      return;
    }

    module.handleWidget(payload);
  }

  private static handleFeedbackSubmit(payload: FeedbackSubmitPayload) {
    // Get or create the module for submission
    let module = this.getModule<UserCommentsModule>('userComments');

    if (!module) {
      // Create module just for data collection (no widget)
      if (!this.userCommentsInfo.enabled) {
        this.userCommentsInfo.enabled = true;
      }

      module = new UserCommentsModule();
      module.setup();
      this.modules[module.namespace] = module;
      // Don't call onStart() - we don't want the widget, just data collection
    }

    module.handleSubmit(payload);
  }

  public static reset() {
    this.pause();
    this.purge();
    this.session.reset();
    window.sessionStorage.removeItem(SESSION_ID_KEY);
    this.sessionId = '';
    this.resume();
  }

  public static suspend() {
    if (!this.running) return;
    if (!this.currentStartOptions) {
      Recapt.warn(
        'Tried to persistant pause, but got no start options. This is a bug.',
      );
      return;
    }

    StorageManager.setItem(SESSION_START_OPTIONS_KEY, this.currentStartOptions);
    StorageManager.setItem(SESSION_PERSISTANT_PAUSE_KEY, true);
    this.pause();
  }

  public static continue() {
    if (this.running) return;

    const isPPaused = StorageManager.getItem(SESSION_PERSISTANT_PAUSE_KEY);

    if (!isPPaused) {
      Recapt.warn(
        'Tried to persistant resume, but got no persistent pause. This is a bug.',
      );
      return;
    }

    StorageManager.removeItem(SESSION_PERSISTANT_PAUSE_KEY);

    if (this.hasStartedOnce) {
      //called suspend on the same page load as continue
      StorageManager.removeItem(SESSION_START_OPTIONS_KEY);
      this.resume();
    } else {
      //called continue on another page load than suspend
      const startOptions = StorageManager.getItem(SESSION_START_OPTIONS_KEY);

      if (!startOptions) {
        Recapt.warn(
          'Tried to persistant resume, but got no start options. This is a bug.',
        );
        return;
      }

      StorageManager.removeItem(SESSION_START_OPTIONS_KEY);

      this.start(startOptions);
    }
  }

  private static canStart() {
    const isPPaused = StorageManager.getItem(SESSION_PERSISTANT_PAUSE_KEY);

    if (isPPaused) {
      this.debug('detected persistent pause, cannot start');
      return false;
    }

    return true;
  }

  public static start(opts: StartOptions) {
    if (typeof window === 'undefined') return;
    if (this.running || this.hasStartedOnce) return;

    if (window.self !== window.top) {
      this.warn('Recapt cannot run inside an iframe for security reasons.');
      return;
    }

    this.currentStartOptions = opts;
    if (opts?.debug === true) this.debugMode();

    if (!this.canStart()) return;

    this.running = true;
    this.hasStartedOnce = true;
    this.apiKey = opts.apiKey;

    if (opts.intercomDetected) this.detectedSupportSystems.push('intercom');
    if (opts.zendeskDetected) this.detectedSupportSystems.push('zendesk');

    this.debug('got start opts', opts);

    if (!hasSessionStorage()) {
      this.warn("Browser doesn't support session storage, shutting down.");
      return;
    }

    this.info('starting Recapt');

    if (
      StorageManager.getItem(MANUAL_SESSION_SAVE_SESSION_KEY) === true &&
      RequestManager.painpointMode === 'manual'
    ) {
      this.saveSession();
    }

    StorageManager.doLoad();

    this.session.init();

    this.sessionId = window.sessionStorage.getItem(SESSION_ID_KEY) || '';

    RequestManager.start();

    if (this.userCommentsInfo.enabled) {
      this.modulesToLoad.push(UserCommentsModule);
    }

    for (const module of this.modulesToLoad) {
      let mod = new module();

      this.debug('setting up module', mod.namespace);

      mod.setup();

      this.modules[mod.namespace] = mod;
    }

    if (opts.recordOptions) {
      const mod = this.getModule<RecordModule>('record');
      if (mod) mod.setOptions(opts.recordOptions);
    }

    for (const mod of this.modules) {
      mod.onStart();
    }
  }

  public static stop() {
    if (!this.running || !this.hasStartedOnce) return;
    this.running = false;

    RequestManager.stop();

    for (const mod of this.modules) {
      mod.onStop();
    }

    if (!this.persistStorage) StorageManager.disablePersist();

    StorageManager.doStore();

    if (this.persistStorage) {
      window.removeEventListener('unload', unloadListener);
    }
  }

  public static resume() {
    if (this.running || !this.hasStartedOnce) return;
    this.running = true;

    RequestManager.start();

    this.session.init();

    for (const mod of this.modules) {
      mod.onResume();
    }
  }

  public static pause() {
    if (!this.running || !this.hasStartedOnce) return;
    this.running = false;

    RequestManager.stop();

    for (const mod of this.modules) {
      mod.onPause();
    }
  }

  /**
   * Enables persistence of data across page unloads.
   * Must be called before Recapt starts.
   */
  public static persist() {
    if (this.hasStartedOnce) {
      this.warn('persist() can only be called before Recapt has started.');
      return;
    }
    this.persistStorage = true;
    window.addEventListener('unload', unloadListener);
  }

  public static onFatalError() {
    this.err('Recapt fatal error');

    try {
      this.stop();
    } catch (err) {
      if (this.DEBUG) {
        console.error(
          'Recapt suffered a fatal error & failed to stop, data integrity may be compromised',
        );
        console.error(err);
      }
    }

    //in-case all else fails, lets at least try to stop the request manager one more time
    //we certainly don't want this data.
    try {
      RequestManager.stop();
    } catch (err) {}
  }

  public static destroy() {
    if (!this.hasStartedOnce) return;

    this.running = false;
    RequestManager.stop();

    for (const mod of this.modules) {
      mod.onStop();
      mod.purgeState();
    }

    this.clearAllStorage();

    if (this.persistStorage) {
      window.removeEventListener('unload', unloadListener);
    }
  }

  private static clearAllStorage() {
    this.session.destroy();

    StorageManager.purge();
    StorageManager.removeItem(SESSION_START_OPTIONS_KEY);
    StorageManager.removeItem(SESSION_PERSISTANT_PAUSE_KEY);
    StorageManager.removeItem(SESSION_TAGS_STORAGE_KEY);
    StorageManager.removeItem(OBFUSCATION_SELECTOR_STORAGE_KEY);
    StorageManager.removeItem(RECORD_ENGINE_STORAGE_KEY);

    if (hasSessionStorage()) {
      window.sessionStorage.removeItem(SESSION_ID_KEY);
    }

    this.sessionId = '';
    this.hasStartedOnce = false;
  }

  public static debug(...rest: any[]) {
    logDebug(this.DEBUG, ...rest);
  }
  public static info(...rest: any[]) {
    logInfo(this.DEBUG, ...rest);
  }
  public static warn(...rest: any[]) {
    logWarn(this.DEBUG, ...rest);
  }
  public static err(...rest: any[]) {
    logError(this.DEBUG, ...rest);
  }

  public static getCurrentSessionTimestamp() {
    const recordModule = this.getModule<RecordModule>('record');
    if (!recordModule) {
      Recapt.err('Record module not found');
      return Date.now();
    }
    return recordModule.getRenderTimestamp(Date.now());
  }
}

const unloadListener = () => {
  Recapt.stop();
};
